import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { BooleanInput, AutocompleteArrayInput, AutocompleteInput, ReferenceInput, required, TextInput, useTranslate, usePermissions } from 'react-admin';
import { useDeviceGroups } from '@/composables/useDeviceGroups.js';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import PropTypes from 'prop-types';
import { DateRangeInput } from '@/components/global/inputs/DateRangeInput.jsx';
import { CustomTimePicker } from '@/components/global/inputs/TimePicker';
import CampaignImageInput from '@/components/adsCampaings/CampaignImageInput.jsx';
import { getDateRangeLabel } from '@/helpers/date/dateHelpers';
import { useFormValueSetter } from '@/composables/useFormValueSetter';
import { styled } from '@mui/material/styles';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints.js';
import { Palette } from '@/themes/themeOverrides/Palette';
import { contractorFilterToQuery } from '@/helpers/filters/filterToQuery.js';

const StyledInputRow = styled(Box)(() => ({
    display: 'flex',
    gap: '24px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '24px',
    [`@media (max-width: ${breakpoints.values.sm}px)`]: {
        gap: '8px',
    },
    '& .date-range-wrapper': {
        flex: 1,
        maxWidth: '236px',
        minWidth: '100px',
    },
    '& .permanent-checkbox': {
        '& .MuiFormControlLabel-label': {
            fontSize: '14px',
        },
    },
}));

const StyledHoursRow = styled(Box)(() => ({
    display: 'flex',
    gap: '24px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '24px',
    [`@media (max-width: ${breakpoints.values.sm}px)`]: {
        gap: '8px',
    },
    '& .hours-label': {
        fontSize: '14px',
        fontWeight: 400,
        color: Palette.gray.gray900,
    },
    '& .time-picker-group': {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexWrap: 'wrap',
        minWidth: '152px',
    },
    '& .time-picker-wrapper': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        '& .time-picker-label': {
            fontSize: '12px',
            color: Palette.gray.gray500,
        },
    },
    '& .continuous-checkbox': {
        '& .MuiCheckbox-root': {
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                paddingLeft: '0',
            },
        },
        '& .MuiFormControlLabel-label': {
            fontSize: '14px',
        },
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            marginLeft: 0,
        },
    },
}));

const StyledSelectionRow = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
        width: '100%',
    },
}));

export const AdvertisementFormContent = ({ imageLoading = false }) => {
    const translate = useTranslate();
    const setFormValue = useFormValueSetter();
    const { clearErrors } = useFormContext();
    const { errors } = useFormState({ name: ['hourTo'] });
    const formValues = useWatch();
    const hasHourToError = Boolean(errors.hourTo);
    const { data: deviceGroups = [] } = useDeviceGroups();
    const { permissions } = usePermissions();

    const deviceGroupChoices = useMemo(() => {
        if (!formValues.contractorId) {
            return [];
        }

        return deviceGroups.filter(group => group.contractorId === formValues.contractorId);
    }, [deviceGroups, formValues.contractorId]);

    const handleHourFromChange = useCallback(
        time => {
            setFormValue('hourFrom', time);
            clearErrors(['hourFrom', 'hourTo']);
        },
        [setFormValue, clearErrors],
    );

    const handleHourToChange = useCallback(
        time => {
            setFormValue('hourTo', time);
            clearErrors('hourTo');
        },
        [setFormValue, clearErrors],
    );

    const handleContinuousChange = useCallback(
        (_, checked) => {
            setFormValue('isContinuous', checked);
            if (checked) {
                setFormValue('hourFrom', undefined);
                setFormValue('hourTo', undefined);
            }
        },
        [setFormValue],
    );

    const handleDateChange = useCallback(
        ({ start, end }) => {
            setFormValue('startDate', start);
            setFormValue('endDate', end);
        },
        [setFormValue],
    );

    const handlePermanentChange = useCallback(
        (_, checked) => {
            setFormValue('isPermanent', checked);
            if (checked) {
                setFormValue('startDate', undefined);
                setFormValue('endDate', undefined);
            }
        },
        [setFormValue],
    );

    const dateRangeLabel = useMemo(() => {
        return getDateRangeLabel(formValues.startDate, formValues.endDate, translate('resources.adsCampaign.labels.emissionTime'), translate);
    }, [formValues.startDate, formValues.endDate, translate]);

    const isDeviceGroupFilled = useMemo(() => {
        const ids = formValues.deviceGroupIds || [];
        return Array.isArray(ids) ? ids.length > 0 : false;
    }, [formValues.deviceGroupIds]);

    useEffect(() => {
        if (!formValues.contractorId) {
            setFormValue('deviceGroupIds', []);
            clearErrors('deviceGroupIds');
        }
    }, [formValues.contractorId, setFormValue, clearErrors]);

    return (
        <Box
            className='form-content'
            sx={{
                '&::after': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    bottom: '-36px',
                    left: '-56px',
                    width: 'calc(100% + 112px)',
                    height: '2px',
                    backgroundColor: Palette.gray.gray100,
                    [`@media (max-width: ${breakpoints.values.sm}px)`]: {
                        bottom: '-30px',
                        left: '-20px',
                        width: 'calc(100% + 40px)',
                    },
                },
            }}
        >
            <Box className='autocomplete-parent' sx={{ marginBottom: '16px' }}>
                <ReferenceInput source='contractorId' reference='contractor'>
                    <AutocompleteInput
                        filterToQuery={contractorFilterToQuery}
                        validate={required(translate('common.validation.required'))}
                        fullWidth={true}
                        disablePortal={true}
                        label={translate('common.contractor')}
                    />
                </ReferenceInput>
            </Box>
            <TextInput
                source='name'
                label={translate('common.name')}
                fullWidth
                validate={required(translate('common.validation.required'))}
                sx={{ marginBottom: '24px' }}
            />
            <StyledInputRow>
                <Box className='date-range-wrapper' sx={{ '& .rs-picker .rs-input-group.rs-input-group-inside': { padding: '3px' } }}>
                    <DateRangeInput
                        start={formValues.startDate}
                        end={formValues.endDate}
                        onChange={handleDateChange}
                        label={dateRangeLabel}
                        disabled={formValues.isPermanent}
                        showOneCalendar={true}
                    />
                </Box>
                <FormControlLabel
                    className='permanent-checkbox'
                    control={<Checkbox checked={!!formValues.isPermanent} onChange={handlePermanentChange} />}
                    label={translate('resources.adsCampaign.labels.permanentAdNoLimits')}
                />
            </StyledInputRow>

            <StyledHoursRow>
                <Typography className='hours-label'>{translate('resources.adsCampaign.labels.displayHours')}</Typography>
                <Box className='time-picker-group'>
                    <Box className='time-picker' sx={{ position: 'relative' }}>
                        <span className='range-picker-label'>{translate('resources.adsCampaign.labels.from')}</span>
                        <CustomTimePicker value={formValues.hourFrom} onChange={handleHourFromChange} disabled={formValues.isContinuous} />
                    </Box>
                    <Box className='time-picker' sx={{ position: 'relative' }}>
                        <span className='range-picker-label'>{translate('resources.adsCampaign.labels.to')}</span>
                        <CustomTimePicker value={formValues.hourTo} onChange={handleHourToChange} disabled={formValues.isContinuous} error={hasHourToError} />
                    </Box>
                </Box>
                <FormControlLabel
                    className='continuous-checkbox'
                    control={<Checkbox checked={!!formValues.isContinuous} onChange={handleContinuousChange} />}
                    label={translate('resources.adsCampaign.labels.continuous')}
                />
            </StyledHoursRow>

            <CampaignImageInput
                source='advertisementImage'
                accept='.jpg,.jpeg,.png'
                instructionText={translate('resources.adsCampaign.labels.imageInstruction')}
                loading={imageLoading}
                disabled={imageLoading}
            />

            <StyledSelectionRow>
                <Box
                    className='autocomplete-parent'
                    sx={{ '& .MuiAutocomplete-inputRoot': { padding: '8px 11px !important', '& .MuiInputBase-input': { minHeight: '30px' } } }}
                >
                    <AutocompleteArrayInput
                        source='deviceGroupIds'
                        choices={deviceGroupChoices}
                        label={translate('menu.deviceGroups')}
                        fullWidth
                        disablePortal={true}
                        optionText='name'
                        optionValue='id'
                        disabled={!formValues.contractorId}
                        className={isDeviceGroupFilled ? 'input-filled' : ''}
                    />
                </Box>
            </StyledSelectionRow>

            {permissions?.isAdmin && (
                <Box sx={{ marginLeft: '10px', marginTop: '16px' }}>
                    <BooleanInput name='isActive' size={'small'} source='isActive' defaultValue={true} label={translate('common.active')} />
                </Box>
            )}
        </Box>
    );
};

AdvertisementFormContent.propTypes = {
    imageLoading: PropTypes.bool,
};
